import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApplicationEvent } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { TimelineService } from '@workspace-app/shared/timeline/services/timeline.service';

@Injectable({
  providedIn: 'root',
})
export class LicenseTimelineService extends TimelineService {
  constructor() {
    super();
  }

  protected fetchEntityData(_entityType: string, _entityId: string): Observable<{ entity: { notes: unknown[] } }> {
    // Licenses don't currently have notes, so return empty notes array
    // In the future, if licenses get notes support, fetch them here
    return of({
      entity: {
        notes: [],
      },
    });
  }

  protected override fetchApplicationEvents(entityId: string, entityTypes: string[]): Observable<ApplicationEvent[]> {
    // For licenses, we need to handle multiple entity types related to licenses
    const licenseTypes = ['License', 'CoaterLicense', 'LicenseRenewalWorkflow'];

    // Combine requested entity types with license-specific types
    const allTypes = [...new Set([...entityTypes, ...licenseTypes])];

    const typeFilter = allTypes.map((type) => `entityType eq '${type}'`).join(' or ');

    return this.applicationEventsService
      .entities()
      .query((q) => {
        q.filter(`entityId eq ${entityId} and (${typeFilter})`);
        q.orderBy('timestamp desc');
        q.expand('user');
        q.top(500); // Limit to last 500 events for performance
      })
      .fetch()
      .pipe(
        map((response) => response.entities || []),
        catchError((error) => {
          console.error('Failed to fetch license application events:', error);
          return of([]);
        })
      );
  }

  /**
   * Fetch events for related entities (e.g., production site, customer)
   * This will fetch events for the license and any related entities
   */
  fetchRelatedLicenseEvents(
    licenseId: string,
    productionSiteId?: string,
    customerId?: string
  ): Observable<ApplicationEvent[]> {
    const entityIds = [licenseId];
    if (productionSiteId) entityIds.push(productionSiteId);
    if (customerId) entityIds.push(customerId);

    const idFilter = entityIds.map((id) => `entityId eq ${id}`).join(' or ');

    const licenseTypes = [
      'License',
      'CoaterLicense',
      'LicenseRenewalWorkflow',
      'ProductionSite',
      'Customer',
      'CustomerOrganisation',
    ];

    const typeFilter = licenseTypes.map((type) => `entityType eq '${type}'`).join(' or ');

    return this.applicationEventsService
      .entities()
      .query((q) => {
        q.filter(`(${idFilter}) and (${typeFilter})`);
        q.orderBy('timestamp desc');
        q.expand('user');
        q.top(1000); // Increased limit for related entities
      })
      .fetch()
      .pipe(
        map((response) => response.entities || []),
        catchError((error) => {
          console.error('Failed to fetch related license events:', error);
          return of([]);
        })
      );
  }
}
